import type { GateResult } from "@exploitlens/ui-contracts";

import { formatGateStatus } from "../../lib/format";

const GATE_LABEL: Record<GateResult["gate"], string> = {
  baseline: "Baseline",
  "exact-replay": "Exact replay",
  "legitimate-control": "Legitimate control",
  neighborhood: "Neighborhood",
};

/**
 * A gate's status is never conveyed by color alone (accessibility
 * requirement, architecture section 12 / EL-061): each status gets its own
 * symbol and border style in addition to color, so the distinction survives
 * grayscale printing or color-vision deficiency.
 */
const STATUS_PRESENTATION: Record<
  GateResult["status"],
  { symbol: string; border: string; text: string; bg: string }
> = {
  pass: {
    symbol: "✓",
    border: "border-preserved",
    text: "text-preserved",
    bg: "bg-preserved-surface",
  },
  fail: { symbol: "✕", border: "border-loss", text: "text-loss", bg: "bg-loss-surface" },
  inconclusive: {
    symbol: "?",
    border: "border-rule-strong border-dashed",
    text: "text-ink-muted",
    bg: "bg-surface",
  },
};

export function GateBadge({ gate }: { gate: GateResult }) {
  const presentation = STATUS_PRESENTATION[gate.status];
  return (
    <div className={`rounded border ${presentation.border} ${presentation.bg} p-4`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-ink">{GATE_LABEL[gate.gate]}</span>
        <span className={`flex items-center gap-1.5 text-sm font-medium ${presentation.text}`}>
          <span aria-hidden="true">{presentation.symbol}</span>
          {formatGateStatus(gate.status)}
        </span>
      </div>
      <p className="mt-2 text-sm text-ink-muted">{gate.explanation}</p>
    </div>
  );
}
